import attachAttributionParams from './middleware/attachAttributionParams';
import attachWindowParams from './middleware/attachWindowParams';
import createMaybeAddUserAndIdentityId from './middleware/createMaybeAddUserAndIdentityId';
import createTrackingFunctions from './createTrackingFunctions';
import environmentParams from './middleware/environmentParams';
import eventDebugger from './middleware/eventDebugger';
import sendToBackend from './middleware/sendToBackend';
import sendToGA from './middleware/sendToGA';
import utmParams from './middleware/utmParams';
import { trackingScopeApi } from './trackingScope';
import { isProduction } from './utils';
import { TrackingEvent, TrackingFunctions, TrackingMiddleware } from './types';
/**
 * Creates a middleware chain that can be used to fire tracking events.
 *
 * Supports passing extra middleware that will fire after a collection of
 * default middleware which retrieves attribution utm params, environmental
 * variables such as path, and the user id and before middleware that sends
 * the values to the backend and google analytics.
 *
 * Example usage:
 *
 * export const { trackEvent } = createEventTracker([
 *     extraMiddleware,
 *   ],
 * );
 *
 * Track an event using the v3 Events API (pre-Avro).  trackEvent defaults to 'v3' but
 * will flip to 'v4' as the default in a future version.
 *
 * trackEvent('hello-world', { foo: 'bar' })
 *
 * Track an event using the v4 Events API (Avro based events)
 * When a sufficient number of events are migrated to Avro, this will become the
 * default mode of operation.
 *
 * Avro based event names all start with 'schema:' followed by the full Avro schema name
 * (The namespace.name value from the Avro schema file)
 *
 * trackEvent('schema:event.zapier.example.HelloWorldEvent', { foo: 'bar' })
 */

type ContextType = { [s: string]: any };

const createEventTracker = (
  customMiddleware: Array<TrackingMiddleware> = []
): TrackingFunctions => {
  const middleware = (isProduction()
    ? [
        attachWindowParams,
        attachAttributionParams,
        utmParams,
        createMaybeAddUserAndIdentityId,
        ...customMiddleware,
        environmentParams,
        sendToBackend,
        sendToGA,
      ]
    : [
        attachWindowParams,
        attachAttributionParams,
        utmParams,
        createMaybeAddUserAndIdentityId,
        ...customMiddleware,
        environmentParams,
        eventDebugger,
      ]
  ).map(ware => ware(trackingScopeApi));
  console.warn(
    'This package has been deprecated. We strongly encourage if you are ' +
      'planning to use it for the first time, please use the new library ' +
      'Events-Toolkit-Browser: https://github.com/zapier/events-toolkit-js'
  );

  return createTrackingFunctions(async (eventName, metaData) => {
    let context: ContextType = {};
    let isCancelled = false;

    const setIsCancelled = () => {
      isCancelled = true;
    };

    const applyContext = (newCtx: ContextType) => {
      context = {
        ...context,
        ...newCtx,
      };
    };

    const _deprecatedPropertiesInRoot: Set<string> = new Set();

    const _deprecatedSetProprtiesInRoot = (properties: Array<any>) => {
      properties.forEach((property: any) => {
        _deprecatedPropertiesInRoot.add(property);
      });
    };

    const _deprecatedBuildProprtiesInRoot = () => {
      const rootContext: { [s: string]: any } = {};

      _deprecatedPropertiesInRoot.forEach(key => {
        rootContext[key] = context[key];
      });

      return rootContext;
    };

    const buildEventApi = (): TrackingEvent => ({
      name: eventName,
      data: {
        ...metaData,
      },
      context: {
        ...context,
      },
      apply: applyContext,
      cancel: setIsCancelled,
      _deprecatedSetProprtiesInRoot,
      _deprecatedBuildProprtiesInRoot,
    });

    try {
      for (const promise of middleware) {
        const event: TrackingEvent = buildEventApi();
        await promise(event);

        if (isCancelled) {
          break;
        }
      }
    } catch (e) {
      if (isProduction()) {
        console.warn(
          JSON.stringify({
            message: 'Unable to track event: ',
            eventName,
            data: metaData,
            context,
          }),
          e
        );
      }
    }
  });
};

export default createEventTracker;
